import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useAlert, useUpdateAlert, useAlertDevices, useAlertRecipients } from '@/hooks/useAlerts'
import { deviceService } from '@/services/device.service'
import { userService } from '@/services/user.service'
import { useToast } from '@/components/ui/use-toast'
import { ArrowLeft } from 'lucide-react'
import type { Device } from '@/types/device.types'
import type { User } from '@/types/user.types'
import type { ComparisonOperator, SeverityLevel } from '@/types/alert.types'

const alertSchema = z.object({
  alertName: z.string().min(1, 'Name is required'),
  deviceType: z.enum(['watersense', 'rk900', 'dht11soil']).optional(),
  measurementColumn: z.string().min(1, 'Parameter is required'),
  comparisonOperator: z.enum(['>', '<', '>=', '<=', '==', '!=']),
  thresholdValue: z.coerce.number(),
  severityLevel: z.enum(['info', 'warning', 'critical']),
  alertMessage: z.string().min(1, 'Message is required'),
  alertLinkedUsers: z.boolean().default(false),
  isActive: z.boolean().default(true),
  deviceIds: z.array(z.string()).default([]),
  recipientUserIds: z.array(z.string()).default([])
})

type AlertFormData = z.infer<typeof alertSchema>

const COMPARISON_OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '>=', label: 'Greater or equal' },
  { value: '<=', label: 'Less or equal' },
  { value: '==', label: 'Equal to' },
  { value: '!=', label: 'Not equal to' }
]

const SEVERITY_LEVELS: { value: SeverityLevel; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' }
]

const MEASUREMENT_COLUMNS: Record<string, { value: string; label: string }[]> = {
  watersense: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'moisture', label: 'Moisture' },
    { value: 'battery', label: 'Battery Level' }
  ],
  rk900: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'pressure', label: 'Pressure' },
    { value: 'battery', label: 'Battery Level' }
  ],
  dht11soil: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'soil_moisture', label: 'Soil Moisture' },
    { value: 'battery', label: 'Battery Level' }
  ]
}

export const AlertEditPage = () => {
  const { alertId } = useParams<{ alertId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data: alert, isLoading: alertLoading } = useAlert(alertId!)
  const { data: alertDevices } = useAlertDevices(alertId!)
  const { data: alertRecipients } = useAlertRecipients(alertId!)
  const updateAlert = useUpdateAlert()

  const [devices, setDevices] = useState<Device[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [isLoadingDevices, setIsLoadingDevices] = useState(true)
  const [isLoadingUsers, setIsLoadingUsers] = useState(true)

  const form = useForm<AlertFormData>({
    resolver: zodResolver(alertSchema),
    defaultValues: {
      alertName: '',
      measurementColumn: '',
      comparisonOperator: '>',
      thresholdValue: 0,
      severityLevel: 'warning',
      alertMessage: '',
      alertLinkedUsers: false,
      isActive: true,
      deviceIds: [],
      recipientUserIds: []
    }
  })

  const watchedDeviceType = form.watch('deviceType')

  // Fetch devices and users
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await deviceService.getDevices()
        setDevices(response.devices || [])
      } catch {
        console.error('Failed to fetch devices')
      } finally {
        setIsLoadingDevices(false)
      }
    }

    const fetchUsers = async () => {
      try {
        const response = await userService.getUsers()
        setUsers(response.users || [])
      } catch {
        console.error('Failed to fetch users')
      } finally {
        setIsLoadingUsers(false)
      }
    }

    fetchDevices()
    fetchUsers()
  }, [])

  // Reset form when alert data loads
  useEffect(() => {
    if (alert && alertDevices && alertRecipients) {
      form.reset({
        alertName: alert.alertName,
        deviceType: alert.deviceType,
        measurementColumn: alert.measurementColumn,
        comparisonOperator: alert.comparisonOperator,
        thresholdValue: alert.thresholdValue,
        severityLevel: alert.severityLevel,
        alertMessage: alert.alertMessage,
        alertLinkedUsers: alert.alertLinkedUsers,
        isActive: alert.isActive,
        deviceIds: alertDevices.map(d => d.id),
        recipientUserIds: alertRecipients.map(r => r.id)
      })
    }
  }, [alert, alertDevices, alertRecipients, form])

  const onSubmit = async (data: AlertFormData) => {
    try {
      const apiData = {
        alert_name: data.alertName,
        device_type: data.deviceType,
        measurement_column: data.measurementColumn,
        comparison_operator: data.comparisonOperator,
        threshold_value: data.thresholdValue,
        severity_level: data.severityLevel,
        alert_message: data.alertMessage,
        alert_linked_users: data.alertLinkedUsers,
        is_active: data.isActive,
        device_ids: data.deviceIds,
        recipient_user_ids: data.recipientUserIds
      }

      await updateAlert.mutateAsync({ alertId: alertId!, alert: apiData as any })
      toast({ title: 'Success', description: 'Alert updated successfully' })
      navigate(`/alerts/${alertId}`)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update alert',
        variant: 'destructive'
      })
    }
  }

  if (alertLoading) return <LoadingSpinner />

  const filteredDevices = watchedDeviceType
    ? devices.filter(d => d.deviceType === watchedDeviceType)
    : devices

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/alerts/${alertId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Edit Alert</h1>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Alert Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="alertName">Alert Name *</Label>
                <Input id="alertName" {...form.register('alertName')} />
                {form.formState.errors.alertName && (
                  <p className="text-sm text-destructive">{form.formState.errors.alertName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Device Type</Label>
                <Controller
                  name="deviceType"
                  control={form.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="All device types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">All device types</SelectItem>
                        <SelectItem value="watersense">WaterSense</SelectItem>
                        <SelectItem value="rk900">RK900</SelectItem>
                        <SelectItem value="dht11soil">DHT11 Soil</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Measurement Column *</Label>
                {watchedDeviceType && MEASUREMENT_COLUMNS[watchedDeviceType] ? (
                  <Controller
                    name="measurementColumn"
                    control={form.control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select measurement" />
                        </SelectTrigger>
                        <SelectContent>
                          {MEASUREMENT_COLUMNS[watchedDeviceType].map(col => (
                            <SelectItem key={col.value} value={col.value}>{col.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <Input {...form.register('measurementColumn')} />
                )}
                {form.formState.errors.measurementColumn && (
                  <p className="text-sm text-destructive">{form.formState.errors.measurementColumn.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Operator *</Label>
                <Controller
                  name="comparisonOperator"
                  control={form.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARISON_OPERATORS.map(op => (
                          <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Threshold *</Label>
                <Input type="number" step="0.01" {...form.register('thresholdValue')} />
                {form.formState.errors.thresholdValue && (
                  <p className="text-sm text-destructive">{form.formState.errors.thresholdValue.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Severity *</Label>
                <Controller
                  name="severityLevel"
                  control={form.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITY_LEVELS.map(level => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea rows={3} {...form.register('alertMessage')} />
              {form.formState.errors.alertMessage && (
                <p className="text-sm text-destructive">{form.formState.errors.alertMessage.message}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Controller
                name="isActive"
                control={form.control}
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label>Active</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Devices & Recipients</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Select Devices</Label>
              <div className="border rounded-md p-4 max-h-48 overflow-y-auto space-y-2">
                {isLoadingDevices ? (
                  <p className="text-sm text-muted-foreground">Loading devices...</p>
                ) : filteredDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No devices available</p>
                ) : (
                  <Controller
                    name="deviceIds"
                    control={form.control}
                    render={({ field }) => (
                      <>
                        {filteredDevices.map(device => (
                          <div key={device.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`device-${device.id}`}
                              checked={field.value?.includes(device.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), device.id]
                                  : field.value?.filter(id => id !== device.id) || []
                                field.onChange(newValue)
                              }}
                            />
                            <Label htmlFor={`device-${device.id}`} className="text-sm font-normal cursor-pointer">
                              {device.name} ({device.serialNumber})
                            </Label>
                          </div>
                        ))}
                      </>
                    )}
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Recipients</Label>
              <div className="border rounded-md p-4 max-h-48 overflow-y-auto space-y-2">
                {isLoadingUsers ? (
                  <p className="text-sm text-muted-foreground">Loading users...</p>
                ) : users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No users available</p>
                ) : (
                  <Controller
                    name="recipientUserIds"
                    control={form.control}
                    render={({ field }) => (
                      <>
                        {users.map(user => (
                          <div key={user.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`user-${user.id}`}
                              checked={field.value?.includes(user.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), user.id]
                                  : field.value?.filter(id => id !== user.id) || []
                                field.onChange(newValue)
                              }}
                            />
                            <Label htmlFor={`user-${user.id}`} className="text-sm font-normal cursor-pointer">
                              {user.firstName} {user.lastName} ({user.email})
                            </Label>
                          </div>
                        ))}
                      </>
                    )}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Controller
                name="alertLinkedUsers"
                control={form.control}
                render={({ field }) => (
                  <Checkbox
                    id="alertLinkedUsers"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="alertLinkedUsers">
                Alert linked users (notify users linked to the device's account)
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to={`/alerts/${alertId}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={updateAlert.isPending}>
            {updateAlert.isPending ? 'Updating...' : 'Update Alert'}
          </Button>
        </div>
      </form>
    </div>
  )
}
